import PropTypes from 'prop-types'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import categoryDuck from 'components/category/category-item/duck'
import {
  getSortFilterOptions,
  loadMoreProductsUtil,
  isNewCategoryFiltersAndSort,
} from 'pages/category/util'
import CategoryLayout from 'components/shared/category/layout'
import { withI18next } from 'lib/i18n/withI18next'
import compose from 'recompose/compose'
import CategoryApiHandler from 'lib/api/category'
import productListDuck from 'components/product/product-list/duck'
import filterDuck from 'components/filter/filter-options/duck'
import InfiniteScroll from 'react-infinite-scroll-component'
import { CategoryProductList } from 'pages/category/list'
import Router from 'next/router'
import { DISABLE_NAV_PROGRESS } from 'constants/cookies'
import { PRODUCTS_PER_PAGE } from 'pages/category/const'
import StateStorage from 'lib/state-storage'
import { SEGMENT_REF_TYPES } from 'lib/segment/const'
import { getProductIDs } from 'lib/utils/handle-response'
import { segmentTrackProductListViewed } from 'lib/segment'
import ProductListSkeleton from 'components/product/product-list-skeleton'
import { PRODUCT_LIST_LAYOUT_VIEW } from 'components/visibility/duck'
import Button from 'components/common/button'
import { hasLocizeTranslation } from 'lib/utils/locize'
import homepageDuck from '../../duck'

export const HomepageCategory = ({
  t,
  sort,
  view,
  filter,
  isPhone,
  category,
  cleanFilters,
  fetchCategory,
  updateFilters,
  categoryProducts,
  invalidateProducts,
  addCategoryProducts,
  internationalization,
  selectedTopNavigation,
  fetchCategoryProducts,
  setIsHomepageCategoryPage,
}) => {
  // states
  const selectedCatId = Number(selectedTopNavigation?.attributes?.value)
  const categoryId = category?.id_category
  const feedId = selectedTopNavigation?.id
  const totalProducts = category?.total_products ?? 0
  const currentTotalProducts = categoryProducts?.products?.length ?? 0
  const isHasMoreProducts = currentTotalProducts < totalProducts

  const [relatedCategories, setRelatedCategories] = useState(null)
  const [isFetchingProducts, setIsFetchingProducts] = useState(false)
  const [isRouting, setIsRouting] = useState(false)
  const paginationInFlight = useRef(false)
  const sortFilterOptions = getSortFilterOptions(category, category?.filters)
  const shouldFetchCategoryProducts =
    categoryId &&
    selectedCatId === categoryId &&
    !categoryProducts?.products?.length &&
    !isRouting

  // events
  const setPageOne = async (resetPageDesktop) => {
    if (!resetPageDesktop) {
      return
    }

    const segmentAnonymousID = StateStorage.getSegmentAnonymousId()

    invalidateProducts()

    const res = await Promise.all([
      fetchCategoryProducts({
        categoryId,
        currencyId: internationalization.currency,
        filter: [],
        limit: PRODUCTS_PER_PAGE,
        segmentAnonymousID,
        shopId: internationalization.shop,
        skip: 0,
        sort,
      }),
      fetchCategory({ categoryId, filter: [] }),
    ])

    if (res?.length) {
      if (res[0]?.error || res[1]?.error) {
        // eslint-disable-next-line
        console.error(error)
      } else if (res[0]?.products?.length > 0) {
        const productIds = getProductIDs(res[0].products)

        segmentTrackProductListViewed(
          {
            id_site_category: categoryId,
            page_number: 1,
            product_list_ids: productIds,
            site_category_name: category.name || '',
            sort_by: sort.by,
          },
          {
            ref_type: SEGMENT_REF_TYPES.APP_FEED,
            ref_id: feedId,
          },
        )
      }
    }
  }

  const loadMoreProducts = async () => {
    if (
      isRouting ||
      isFetchingProducts ||
      !isHasMoreProducts ||
      paginationInFlight.current
    )
      return

    paginationInFlight.current = true
    try {
      await loadMoreProductsUtil({
        sort,
        filter,
        category,
        startPageNo: 1,
        categoryProducts,
        addCategoryProducts,
        internationalization,
        setIsFetchingProducts,
        setApiResError: () => {},
        user_uid: StateStorage.getUserUID(),
        segment_anonymous_id: StateStorage.getSegmentAnonymousId(),
        ref: {
          ref_type: SEGMENT_REF_TYPES.APP_FEED,
          ref_id: feedId,
        },
      })
    } finally {
      paginationInFlight.current = false
      setIsFetchingProducts(false)
    }
  }

  const onProductPageChanged = () => {
    // Handle the switching back and forward between category and non-category page issue.
    if (window.location.pathname !== Router.asPath.split('?')[0]) {
      return
    }

    sessionStorage.setItem(DISABLE_NAV_PROGRESS, '1')
  }

  const getRelatedCategories = useCallback(async (id_category) => {
    try {
      const res = await CategoryApiHandler.getCategoryRelated(id_category)

      if (res) {
        setRelatedCategories(res)
      }
    } catch (error) {
      // eslint-disable-next-line
      console.error(error)
      setRelatedCategories({})
    }
  }, [])

  // effects
  useEffect(() => {
    const loadCategory = async () => {
      setIsHomepageCategoryPage(true)
      cleanFilters()
      await invalidateProducts()

      const [categoryData] = await Promise.all([
        fetchCategory({ categoryId: selectedCatId }),
        fetchCategoryProducts({
          sort,
          filter,
          skip: 0,
          limit: 54,
          categoryId: selectedCatId,
          shopId: internationalization.shop,
          currencyId: internationalization.currency,
          segmentAnonymousID: StateStorage.getSegmentAnonymousId(),
        }),
        getRelatedCategories(selectedCatId),
      ])

      if (categoryData) {
        await updateFilters(categoryData?.filters)
      }
    }

    if (selectedCatId) {
      loadCategory()
    }

    return () => {
      // reset upon page leave
      setIsHomepageCategoryPage(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCatId])

  useEffect(() => {
    const loadCategoryProducts = async () => {
      const res = await fetchCategoryProducts({
        sort,
        filter,
        skip: 0,
        categoryId,
        limit: 54,
        shopId: internationalization.shop,
        currencyId: internationalization.currency,
        segmentAnonymousID: StateStorage.getSegmentAnonymousId(),
      })

      // Note: Trigger product list view event here instead of relying on componentDidMount inside the CategoryProductList
      if (res) {
        const productIds = getProductIDs(res?.products)

        segmentTrackProductListViewed(
          {
            id_site_category: categoryId,
            page_number: 1,
            product_list_ids: productIds,
            site_category_name: category.name || '',
            sort_by: sort.by,
          },
          {
            ref_type: SEGMENT_REF_TYPES.APP_FEED,
            ref_id: feedId,
          },
        )
      }
    }

    if (shouldFetchCategoryProducts) {
      loadCategoryProducts()
    }
  }, [
    sort,
    filter,
    feedId,
    categoryId,
    category?.name,
    internationalization,
    fetchCategoryProducts,
    shouldFetchCategoryProducts,
  ])

  useEffect(() => {
    const handleRouteChangeStart = () => {
      setIsRouting(true)
    }

    Router.events.on('routeChangeStart', handleRouteChangeStart)

    return () => {
      Router.events.off('routeChangeStart', handleRouteChangeStart)
    }
  }, [])

  return (
    <CategoryLayout
      t={t}
      isHomePage
      quickSizes={[]}
      isPhone={isPhone}
      setPageOne={setPageOne}
      categoryId={selectedCatId}
      totalProducts={totalProducts}
      sortFilterOptions={sortFilterOptions ?? {}}
      relatedCategories={relatedCategories?.children || null}
      isNewCategoryFiltersAndSort={isNewCategoryFiltersAndSort(
        category?.filters,
      )}
      refId={feedId}
      refType={SEGMENT_REF_TYPES.APP_FEED}
    >
      {categoryProducts?.products ? (
        <InfiniteScroll
          scrollThreshold={0.7}
          next={loadMoreProducts}
          hasMore={isHasMoreProducts && !isRouting}
          dataLength={categoryProducts?.products.length}
          hasChildren={categoryProducts?.products.length > 0}
        >
          <CategoryProductList
            hideVirtualTryOn
            workwearGrid
            t={t}
            showTeaser
            isPhone={isPhone}
            pageName="category"
            category={category}
            displayQuickAddToBag={
              view === PRODUCT_LIST_LAYOUT_VIEW.BLOCK_VIEW ?? false
            }
            displayHoverInteractions
            refId={feedId}
            refType={SEGMENT_REF_TYPES.APP_FEED}
            list={categoryProducts?.products}
            isFetchingProducts={isFetchingProducts}
            onProductPageChanged={onProductPageChanged}
          />
          {isHasMoreProducts && !isFetchingProducts && (
            <Button
              className="category-body__load-more-cta"
              onClick={loadMoreProducts}
            >
              {hasLocizeTranslation(t, 'Load More', 'Load More')}
            </Button>
          )}
        </InfiniteScroll>
      ) : (
        <ProductListSkeleton number={18} workwearGrid />
      )}
    </CategoryLayout>
  )
}

HomepageCategory.defaultProps = {
  view: null,
  isPhone: false,
  category: null,
  categoryProducts: null,
  selectedTopNavigation: null,
}

HomepageCategory.propTypes = {
  view: PropTypes.string,
  isPhone: PropTypes.bool,
  t: PropTypes.func.isRequired,
  cleanFilters: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  fetchCategory: PropTypes.func.isRequired,
  selectedTopNavigation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    attributes: PropTypes.shape({ value: PropTypes.string }),
  }),
  invalidateProducts: PropTypes.func.isRequired,
  fetchCategoryProducts: PropTypes.func.isRequired,
  setIsHomepageCategoryPage: PropTypes.func.isRequired,
  internationalization: PropTypes.shape({
    shop: PropTypes.number,
    currency: PropTypes.number,
  }).isRequired,
  category: PropTypes.shape({
    name: PropTypes.string,
    id_category: PropTypes.number,
    total_products: PropTypes.number,
    filters: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  categoryProducts: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.shape({})),
    sub_categories: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  addCategoryProducts: PropTypes.func.isRequired,
  filter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  sort: PropTypes.shape({ by: PropTypes.string }).isRequired,
}

export default compose(
  connect(
    (state) => ({
      sort: state.sort,
      filter: state.filter,
      view: state.visibility.view,
      isPhone: state.device.isPhone,
      category: state.category.payload,
      categoryProducts: state.products.payload,
      internationalization: state.internationalization,
      selectedTopNavigation: state.homePage.selectedTopNavigation,
    }),
    (dispatch) =>
      bindActionCreators(
        {
          cleanFilters: filterDuck.creators.clean,
          fetchCategory: categoryDuck.creators.fetch,
          updateFilters: filterDuck.creators.updateFilters,
          addCategoryProducts: productListDuck.creators.add,
          setIsHomepageCategoryPage:
            homepageDuck.creators.setIsHomepageCategoryPage,
          fetchCategoryProducts: productListDuck.creators.get,
          invalidateProducts: productListDuck.creators.invalidate,
          updateCategoryProducts: productListDuck.creators.receive,
        },
        dispatch,
      ),
  ),
  withI18next(),
)(HomepageCategory)
